// ON-clause partner-column affinity (Workstream C ranking signal S3).
//
// At a `join-on` / `where` dotted-access position the strongest ranking hint
// is the column on the OTHER side of the comparison the user is writing:
// `ON s.store_id = i.▮` wants `store_id`; `ON child.manager_id = ec.▮` wants
// `employee_id` (the FK pair of `manager_id`). The endpoint-pair demotion in
// `fkColumnRanking.ts` can't express this — it promotes EVERY FK column of
// the dotted relation (the wrong one wins alphabetically when several tie)
// and is silent entirely when no direct edge links the two bound relations
// (real AdventureWorks has no direct SalesOrderDetail→Product edge — the path
// is two-hop via SpecialOfferProduct — and MERGE staging tables have no FKs
// at all).
//
// Three pure helpers, layered ABOVE the existing demotion by the resolvers:
//  1. `detectComparisonPartnerColumn` — scan the buffer around the dotted
//     base for the comparison partner (backward `x.col = alias.▮` first,
//     then forward `alias.▮word = x.col` for caret-mid-buffer edits).
//  2. `partnerAffinityColumns` — the partner name itself ∪ its FK-paired
//     names ∪ sibling FK columns referencing the same target (name-based, so
//     it works for CTE projections and across multi-hop edges).
//  3. `promotePartnerAffinityColumns` — keep affinity columns at tier 1,
//     demote other tier-1 columns to tier 2.
// Plus the no-partner fallback for FK-less joins (MERGE staging):
//  4. `promoteNameIntersectionPkColumns` — columns shared by name with
//     another bound table rank first, the target's PK among them topmost.
//
// Pure module — no Monaco / I/O. Same direction + schema-match semantics as
// `fkJoinSnippet.ts` / `fkColumnRanking.ts`.

import type { AliasBinding, CompletionCandidate, FkEdge } from '../../../core/engines';
import { schemaMatches } from './fkJoinSnippet';

const IDENT_CHAR = /[A-Za-z0-9_$#@]/;
const COMPARISON_OPS = new Set(['=', '<>', '!=', '<=', '>=', '<', '>']);

/** Last (rightmost) segment of an identifier chain ending at `end` (exclusive). */
function lastChainSegmentBackward(text: string, end: number): string | undefined {
  const ch = text[end - 1];
  if (ch === ']' || ch === '"' || ch === '`') {
    const open = ch === ']' ? '[' : ch;
    const openIdx = text.lastIndexOf(open, end - 2);
    return openIdx >= 0 ? text.slice(openIdx + 1, end - 1) || undefined : undefined;
  }
  let i = end;
  while (i > 0 && IDENT_CHAR.test(text[i - 1]!)) i--;
  if (i === end) return undefined;
  const seg = text.slice(i, end);
  return /^\d+$/.test(seg) ? undefined : seg;
}

function backwardPartner(text: string, offset: number): string | undefined {
  // Walk back over the dotted-base expression the caret sits in.
  let i = offset;
  while (i > 0) {
    const ch = text[i - 1]!;
    if (IDENT_CHAR.test(ch) || ch === '.') {
      i--;
      continue;
    }
    if (ch === ']' || ch === '"' || ch === '`') {
      const open = ch === ']' ? '[' : ch;
      const openIdx = text.lastIndexOf(open, i - 2);
      if (openIdx < 0) return undefined;
      i = openIdx;
      continue;
    }
    break;
  }
  let j = i;
  while (j > 0 && /\s/.test(text[j - 1]!)) j--;
  const opEnd = j;
  while (j > 0 && /[=<>!]/.test(text[j - 1]!)) j--;
  if (!COMPARISON_OPS.has(text.slice(j, opEnd))) return undefined;
  let k = j;
  while (k > 0 && /\s/.test(text[k - 1]!)) k--;
  return lastChainSegmentBackward(text, k);
}

function forwardPartner(text: string, offset: number): string | undefined {
  // Skip the remainder of the token at the caret, then require a plain `=`.
  let i = offset;
  while (i < text.length && IDENT_CHAR.test(text[i]!)) i++;
  while (i < text.length && /\s/.test(text[i]!)) i++;
  if (text[i] !== '=') return undefined;
  i++;
  if (i < text.length && /[=<>]/.test(text[i]!)) return undefined;
  while (i < text.length && /\s/.test(text[i]!)) i++;
  // Read the identifier chain; the partner is its last segment.
  let last: string | undefined;
  while (i < text.length) {
    const ch = text[i]!;
    if (ch === '[' || ch === '"' || ch === '`') {
      const close = ch === '[' ? ']' : ch;
      const closeIdx = text.indexOf(close, i + 1);
      if (closeIdx < 0) break;
      last = text.slice(i + 1, closeIdx);
      i = closeIdx + 1;
    } else if (IDENT_CHAR.test(ch)) {
      const start = i;
      while (i < text.length && IDENT_CHAR.test(text[i]!)) i++;
      last = text.slice(start, i);
    } else {
      break;
    }
    if (text[i] !== '.') break;
    i++;
  }
  if (last === undefined || /^\d+$/.test(last)) return undefined;
  return last;
}

/**
 * The column name on the other side of the comparison the dotted base at
 * `offset` participates in, or undefined when the caret isn't adjacent to
 * one. Backward (`x.col = alias.▮`) wins over forward (`alias.▮word = x.col`
 * — the caret-mid-buffer / re-edit shape).
 */
export function detectComparisonPartnerColumn(
  text: string,
  offset: number,
): string | undefined {
  return backwardPartner(text, offset) ?? forwardPartner(text, offset);
}

/**
 * Lower-cased affinity name-set for a comparison partner: the partner itself,
 * every column FK-paired with it (either direction, by column index), and
 * every sibling FK column referencing the same target column. Name-based on
 * purpose — it works for CTE projections (no FK metadata of their own) and
 * bridges multi-hop FK paths (AdventureWorks SalesOrderDetail→
 * SpecialOfferProduct→Product) that endpoint matching can't see.
 */
export function partnerAffinityColumns(
  partner: string,
  edges: readonly FkEdge[],
): ReadonlySet<string> {
  const p = partner.toLowerCase();
  const result = new Set<string>([p]);
  const targets = new Set<string>();
  const targetKey = (fk: FkEdge, toColumn: string): string =>
    `${(fk.toSchema ?? '').toLowerCase()}|${fk.toTable.toLowerCase()}|${toColumn}`;
  for (const fk of edges) {
    for (let i = 0; i < fk.fromColumns.length; i++) {
      const from = fk.fromColumns[i]?.toLowerCase();
      const to = fk.toColumns[i]?.toLowerCase();
      if (from === undefined || to === undefined) continue;
      if (from === p) {
        result.add(to);
        targets.add(targetKey(fk, to));
      }
      if (to === p) {
        result.add(from);
        targets.add(targetKey(fk, to));
      }
    }
  }
  if (targets.size > 0) {
    for (const fk of edges) {
      for (let i = 0; i < fk.fromColumns.length; i++) {
        const from = fk.fromColumns[i]?.toLowerCase();
        const to = fk.toColumns[i]?.toLowerCase();
        if (from === undefined || to === undefined) continue;
        if (targets.has(targetKey(fk, to))) result.add(from);
      }
    }
  }
  return result;
}

/**
 * Keep tier-1 column candidates whose label is in `affinity` at tier 1 and
 * demote the rest to tier 2. Applies only when at least one candidate
 * matches (returns false otherwise so the caller can fall back to the
 * endpoint-pair demotion). Mutates `out` in place; columns only.
 */
export function promotePartnerAffinityColumns(
  out: CompletionCandidate[],
  affinity: ReadonlySet<string>,
): boolean {
  if (affinity.size === 0) return false;
  const matches = out.some(
    (c) => c.kind === 'column' && c.tier === 1 && affinity.has(c.label.toLowerCase()),
  );
  if (!matches) return false;
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (c.kind !== 'column' || c.tier !== 1) continue;
    if (affinity.has(c.label.toLowerCase())) continue;
    out[i] = { ...c, tier: 2 };
  }
  return true;
}

/** Structural table shape — every resolver's snapshot table satisfies it. */
export type NameIntersectionTable = {
  readonly name: string;
  readonly schema?: string;
  readonly columns?: readonly { readonly name: string; readonly isPrimaryKey?: boolean }[];
};

/**
 * No-partner, no-FK fallback ("column match heuristic" — MERGE staging joins):
 * columns of the dotted relation that share a name with a column of another
 * bound table rank first, the dotted relation's PK among them topmost
 * (PK∩ → tier 1, name∩ → tier 2, rest → tier 3). No-op when there is no
 * intersection, no other table/view binding, or the dotted binding isn't a
 * table/view. Mutates `out` in place; columns only.
 */
export function promoteNameIntersectionPkColumns(
  out: CompletionCandidate[],
  target: AliasBinding,
  bindings: readonly AliasBinding[],
  tables: readonly NameIntersectionTable[],
): void {
  if (target.kind !== 'table' && target.kind !== 'view') return;
  const findTable = (b: AliasBinding): NameIntersectionTable | undefined => {
    const rel = b.relation.toLowerCase();
    const sch = b.schema?.toLowerCase();
    return tables.find(
      (t) => t.name.toLowerCase() === rel && schemaMatches(t.schema?.toLowerCase(), sch),
    );
  };
  const targetTable = findTable(target);
  if (!targetTable?.columns?.length) return;
  const otherNames = new Set<string>();
  for (const b of bindings) {
    if (b.alias.toLowerCase() === target.alias.toLowerCase()) continue;
    if (b.kind !== 'table' && b.kind !== 'view') continue;
    for (const c of findTable(b)?.columns ?? []) otherNames.add(c.name.toLowerCase());
  }
  if (otherNames.size === 0) return;
  const pkShared = new Set<string>();
  const shared = new Set<string>();
  for (const c of targetTable.columns) {
    const lc = c.name.toLowerCase();
    if (!otherNames.has(lc)) continue;
    (c.isPrimaryKey === true ? pkShared : shared).add(lc);
  }
  if (pkShared.size === 0 && shared.size === 0) return;
  for (let i = 0; i < out.length; i++) {
    const c = out[i]!;
    if (c.kind !== 'column' || c.tier !== 1) continue;
    const lc = c.label.toLowerCase();
    if (pkShared.has(lc)) continue;
    out[i] = { ...c, tier: shared.has(lc) ? 2 : 3 };
  }
}
